import React from "react"; // Import React library
import { useHistory } from "react-router-dom"; // Import useHistory hook for navigation
import demoStoremaleIcon from "../../../../assets/Images/man.png"; // Import male icon image
import demoStorefemaleIcon from "../../../../assets/Images/woman.png"; // Import female icon image
import { getEventWithMostOverlaps } from "../../../../constants/constants"; // Import utility to get event with most overlaps
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum"; // Import booking status enum
import { BookingTypeEnum } from "../../../../constants/enums/booking-type-enum"; // Import booking type enum
import { formatMessage } from "../../../../translations/format-message"; // Import message formatting utility
import DraggableEvent from "../draggableEvent"; // Import draggable event component
import AddBreakDetailsPopupHOC from "../hoc/add-break-details-popup-hoc"; // Import HOC for add break details popup
import AllMoreEventsPopupHOC from "../hoc/all-more-events-popup-hoc"; // Import HOC for "All More Events" popup
import ReservationDetailsPopupHOC from "../hoc/reservation-details-popup-hoc"; // Import HOC for reservation details popup
import AllMoreEventsComponent from "../modal-components/all-more-events-component"; // Import "All More Events" modal component
import ReservationDetailsComponent from "../modal-components/reservation-details-component"; // Import reservation details modal component
import EventCSSProperties from "./event-css-properties"; // Import utility for event CSS properties
import IntervalEvent from "./interval-event-component"; // Import interval event component
import ServiceNameDisplayComponent from "./services-name-component"; // Import service name display component

// Define props interface for the component
interface IProps {
  key: string; // Unique key for the component
  day: any; // Day information
  currentDate: any; // Current date
  dataObject: any; // Data object for the event
  timeLabel: any; // Time label
  start: any; // Start time
  end: any; // End time
  overlappingCount: any; // Count of overlapping events
  defaultMargin: any; // Default margin for overlapping events
  allEvents: any[]; // List of all events
  allBreaks: any[]; // List of all breaks
  events: any[]; // List of events
  cellWidth: any; // Width of a cell
  storeId: number; // Store ID
  viewOnly: boolean; // Flag for view-only mode
  breaksCount?: number; // Optional breaks count
  allMatchedBreaks?: any[]; // Optional matched breaks array
  index?: number; // Optional index
  staffList?: any[]; // Optional list of staff options
}

// Main Component
const CompletedEventComponent = (props: any) => {
  const history = useHistory(); // Hook to manage navigation
  const {
    key, // Key for the component
    day, // Day information
    currentDate, // Current date
    dataObject, // Data object for the event
    timeLabel, // Time label
    start, // Start time
    end, // End time
    overlappingCount, // Count of overlapping events
    defaultMargin, // Default margin for overlapping events
    events, // List of events
    allEvents, // List of all events
    allBreaks, // List of all breaks
    cellWidth, // Width of a cell
    viewOnly, // Flag for view-only mode
    breaksCount, // Optional breaks count
    allMatchedBreaks, // Optional matched breaks array
    index, // Optional index
    staffList, // Optional list of staff options
  } = props; // Destructure props

  // Calculate the count of overlapping appointments
  const appointmentsCounts =
    getEventWithMostOverlaps(dataObject, allEvents) !== null // Check if there are overlapping events
      ? `${
          getEventWithMostOverlaps(dataObject, allEvents)?.OverlappedEventsIds
            ?.length - 2
        } ` // Get the count of overlapping events
      : `${dataObject?.Overlapped - 2} `; // Fallback to dataObject's overlapping count

  const shouldShowBreaks = props.breaksCount > 0 ? true : false; // Determine if breaks should be shown

  // const [selectedBreak, setSelectedBreak] = React.useState<any>(); // State for selected break (commented out)

  // Function to open reservation details popup
  const openReservationDetailsPopup = () => {
    props.reservationDetailsToggleHandler(); // Call toggle handler for reservation details popup
  };

  // const openAddBreakDetailsPopup = (data: any) => { // Function to open add break details popup (commented out)
  //   setSelectedBreak(data); // Set selected break
  //   props.addBreakDetailsToggleHandler(); // Call toggle handler for add break details popup
  // };

  // Function to open "All More Events" popup
  const openAllMoreEventsPopup = () => {
    props.allMoreEventsToggleHandler(); // Call toggle handler for "All More Events" popup
  };

  return (
    <React.Fragment key={key}> {/* Fragment to wrap the component */}
      {/* Render "All More Events" popup if enabled */}
      {props.allMoreEventsPopup && (
        <AllMoreEventsComponent
          onModalClosed={props.allMoreEventsToggleHandler} // Close handler for popup
          // addBreakDetailsToggleHandler={openAddBreakDetailsPopup} // Pass add break details handler (commented out)
          matchedEvent={dataObject} // Matched event data
          AllEvents={allEvents} // List of all events
          staffList={staffList} // List of staff options
          breaks={props.allMatchedBreaks} // List of all breaks
          day={day} // Day information
        />
      )}

      {/* Render reservation details popup if enabled */}
      {props.showReservationDetailsPopup && (
        <ReservationDetailsComponent
          onModalClosed={props.reservationDetailsToggleHandler} // Close handler for popup
          event={dataObject} // Event data
          storeId={props.storeId} // Store ID
          viewOnly={viewOnly} // Flag for view-only mode
        />
      )}

      {/* {props.addBreakDetailsPopup && ( // Render add break details popup if enabled (commented out)
        <AddBreakDetailsComponent
          id={selectedBreak?.BreakID} // Passing BreakID to AddBreakDetailsComponent
          staffId={selectedBreak?.StaffId} // Passing staffId to AddBreakDetailsComponent
          staffList={props.staffList} // Passing staffList to AddBreakDetailsComponent
          startTime={selectedBreak?.StartTime} // Passing startTime to AddBreakDetailsComponent
          onModalClosed={props.addBreakDetailsToggleHandler} // Passing toggle handler to AddBreakDetailsComponent
          storeId={props.storeId} // Store ID
          viewOnly={props.viewOnly} // Passing viewOnly for view only mode
        />
      )} */}

      <DraggableEvent
        key={`${
          dataObject?.Id || dataObject?.ReservationId || `${day}-${timeLabel}`
        }`} // Unique key for draggable event
        currentDate={currentDate} // Current date
        area="Inner" // Area type
        data={dataObject} // Event data
        style={EventCSSProperties(
          start, // Start time
          end, // End time
          overlappingCount * defaultMargin, // Calculated margin for overlapping events
          cellWidth * (dataObject?.TotalTime / 5) // Calculated width based on total time
        )}
      >
        {({ onResizingChange }) => ( // Render function for draggable event
          <React.Fragment>
            <div className="event completed">{/* Main event container */}
              <div
                onClick={openReservationDetailsPopup} // Click handler to open reservation details
                style={{ cursor: "pointer" }} // Pointer cursor style
              >
                <div>{dataObject?.CustomerName}</div>{/* Customer name */}
                <div>
                  {dataObject?.StartTime} - {dataObject?.EndTime}{" "} {/* Event time range */}
                </div>
                <div className="complete-event-details"> {/* Container for event details */}
                  {dataObject?.AppointmentStatus == // Check if appointment is completed
                    bookingStatusEnum.Complete && (
                    <span className="badge bg-success"> {/* Badge for completed status */}
                      {formatMessage("completed")} {/* Display completed message */}
                    </span>
                  )}
                  {dataObject?.BookingType == BookingTypeEnum.NewWeb && ( // Check if booking type is NewWeb
                    <span className="event-mode bg-danger position-relative"> {/* Badge for New booking type */}
                      <p className="text-white">New</p> {/* Display "New" text */}
                    </span>
                  )}
                  <span className="event-mode"> {/* Container for booking type icon */}
                    {dataObject?.BookingType == BookingTypeEnum.Web ||
                    dataObject?.BookingType == BookingTypeEnum.NewWeb ? ( // Check if booking type is Web
                      <i className="bi bi-globe"></i> // Globe icon for Web booking
                    ) : dataObject?.BookingType == BookingTypeEnum.Visit ||
                      dataObject?.BookingType == BookingTypeEnum.NewVisit ? ( // Check if booking type is Visit
                      <i className="bi bi-shop-window"></i> // Shop window icon for Visit booking
                    ) : (
                      ""
                    )}
                  </span>
                </div>
                <div className="treatment-name"> {/* Treatment name or additional details */}
                  <ServiceNameDisplayComponent
                    services={dataObject?.ServiceRequestedList} // List of requested services
                  />
                </div>
                <div className="supporting-details my-2 d-flex align-items-center gap-2"> {/* Supporting details container */}
                  {dataObject?.StaffPreference == 3 && ( // Check if staff preference is 3
                    <span className="event-mode nomination-fee">指</span> // Display nomination fee indicator
                  )}
                  {dataObject?.StaffPreference == 1 && ( // Check if staff preference is male
                    <span className="event-mode staff-preference"> {/* Container for male staff icon */}
                      <img src={demoStoremaleIcon} /> {/* Male staff icon */}
                    </span>
                  )}
                  {dataObject?.StaffPreference == 2 && ( // Check if staff preference is female
                    <span className="event-mode staff-preference"> {/* Container for female staff icon */}
                      <img src={demoStorefemaleIcon} /> {/* Female staff icon */}
                    </span>
                  )}
                </div>
              </div>
              <div className="row"> {/* Row for more events link */}
                {(dataObject?.IsMoreEvents == true || // Check if there are more events
                  (events?.length <= 2 && // Check if event count is less than or equal to 2
                    props.breaksCount > 0 && // Check if there are breaks
                    index == 1)) && ( // Check if index is 1
                  <React.Fragment>
                    <div
                      className="col-lg-4 more-events" // Column for more events link
                      style={{ cursor: "pointer" }} // Set pointer cursor
                      onClick={openAllMoreEventsPopup} // Click to open "All More Events" popup
                    >
                      +{" "} {/* Plus sign for more events */}
                      {appointmentsCounts !== "0 " && ( // Show appointments count if not zero
                        <React.Fragment>
                          {appointmentsCounts} {formatMessage("more")}{" "} {/* Display count and "more" message */}
                        </React.Fragment>
                      )}
                      {shouldShowBreaks && ( // Show breaks count if applicable
                        <span className="">
                          {appointmentsCounts !== "0 " && (
                            <React.Fragment> {/* Separator if appointments count is not zero */}
                              {appointmentsCounts} {formatMessage("more")}{" "}
                            </React.Fragment>
                          )}
                          {props.breaksCount}{" "} {/* Display breaks count */}
                          {props.breaksCount > 1 ? ( // Check if breaks count is greater than 1
                            <span>{formatMessage("breaks")}</span> // Display "breaks" message
                          ) : (
                            <span>{formatMessage("break")}</span> // Display "break" message
                          )}
                        </span>
                      )}
                    </div>
                  </React.Fragment>
                )}
              </div>
            </div>
            <IntervalEvent
              day={day} // Day information
              style={cellWidth} // Style for interval event
              onResizingChange={onResizingChange} // Resizing change handler
              dataObject={dataObject} // Event data
              allEvents={allEvents} // List of all events
              allBreaks={allBreaks} // List of all breaks
              disabled={viewOnly} // Disable if in view-only mode
            />
          </React.Fragment>
        )} 
      </DraggableEvent>
    </React.Fragment>
  );
};

// Export wrapped component with HOCs
export default AddBreakDetailsPopupHOC(
  AllMoreEventsPopupHOC(ReservationDetailsPopupHOC(CompletedEventComponent)) // Wrap with HOCs
);
